import { ServerMsgSchema, type ClientMsg, type ServerMsg } from '@rfqsports/shared';
import { useStore } from './store';

const URL_DEFAULT = 'ws://127.0.0.1:7878';

let ws: WebSocket | null = null;
let backoffIdx = 0;
const BACKOFF = [200, 500, 1_000, 2_000, 5_000];

// WS lifecycle logging. Verbose per-event logs are gated behind
// NEXT_PUBLIC_WS_DEBUG so reconnect backoff doesn't spam the console in
// prod; abnormal closes are always warned (see the close handler).
const WS_DEBUG = process.env['NEXT_PUBLIC_WS_DEBUG'] === 'true';
function wlog(event: string, detail?: Record<string, unknown>): void {
  if (!WS_DEBUG) return;
  // eslint-disable-next-line no-console
  console.info('[ws]', event, detail ?? '');
}
function wdebug(event: string, detail?: Record<string, unknown>): void {
  if (!WS_DEBUG) return;
  // eslint-disable-next-line no-console
  console.debug('[ws]', event, detail ?? '');
}

function url(): string {
  return process.env['NEXT_PUBLIC_EXEC_URL'] ?? URL_DEFAULT;
}

export function connectExec(): () => void {
  let stopped = false;

  function open(): void {
    if (stopped) return;
    wlog('connecting', { url: url(), attempt: backoffIdx });
    const sock = new WebSocket(url());
    ws = sock;
    sock.addEventListener('open', () => {
      wlog('open', { url: sock.url });
      backoffIdx = 0;
      useStore.getState().setConnected(true);
      useStore.getState().setReconnectAttempts(0);
      // PR 10 — replay any submits that were in flight when we lost the
      // connection. Dynamic import so the ws-client doesn't pull the
      // vault module (and viem) into the games-page bundle.
      const pending = useStore.getState().pendingSubmits;
      if (pending.size > 0) {
        void import('./vault/submit-actions').then((m) => {
          void m.replayPendingSubmits().then(({ replayed }) => {
            useStore.getState().addError(`reconnected — replayed ${replayed} pending submit(s)`);
          });
        });
      }
    });
    sock.addEventListener('close', (ev) => {
      useStore.getState().setConnected(false);
      // Abnormal closes (anything but normal / going-away / no-status) are
      // always surfaced — e.g. 1008 origin_not_allowed from the executor.
      const abnormal = ev.code !== 1000 && ev.code !== 1001 && ev.code !== 1005;
      const detail = {
        url: sock.url,
        code: ev.code,
        reason: ev.reason || null,
        wasClean: ev.wasClean,
        attempt: backoffIdx + 1,
        stopped,
      };
      if (abnormal && !stopped) {
        // eslint-disable-next-line no-console
        console.warn('[ws] closed', detail);
      } else {
        wlog('closed', detail);
      }
      if (stopped) return;
      // Mark a reconnect attempt scheduled. UI uses this to switch from
      // "reconnecting…" (small attempt count) to "offline" once the gap
      // outlasts Caddy's upstream retry window.
      useStore.getState().setReconnectAttempts(backoffIdx + 1);
      const delay = BACKOFF[Math.min(backoffIdx, BACKOFF.length - 1)] ?? 5_000;
      backoffIdx++;
      wlog('reconnect scheduled', { delayMs: delay, nextAttempt: backoffIdx });
      setTimeout(open, delay);
    });
    sock.addEventListener('error', () => {
      // The browser exposes no detail on WS error events; the following
      // close event carries the code. The close handler will retry.
      wlog('error', { url: sock.url });
    });
    sock.addEventListener('message', (ev) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(typeof ev.data === 'string' ? ev.data : ev.data.toString());
      } catch {
        return;
      }
      const result =
        process.env.NODE_ENV === 'production'
          ? { success: true as const, data: parsed as ServerMsg }
          : ServerMsgSchema.safeParse(parsed);
      if (!result.success) return;
      route(result.data);
    });
  }

  open();
  return () => {
    wlog('teardown (effect cleanup)');
    stopped = true;
    ws?.close();
    ws = null;
  };
}

function route(msg: ServerMsg): void {
  const s = useStore.getState();
  switch (msg.t) {
    case 'hello':
      break;
    case 'markets':
      wdebug('rx markets', { venue: msg.venue, count: msg.markets.length });
      s.setMarkets(msg.venue, msg.markets);
      break;
    case 'events':
      wdebug('rx events', { venue: msg.venue, count: msg.events.length });
      s.setEvents(msg.venue, msg.events);
      break;
    case 'games':
      wdebug('rx games', { count: msg.games.length, matchedAt: msg.matchedAt });
      s.setGames(msg.games, msg.matchedAt);
      break;
    case 'order_book':
      wdebug('rx order_book snapshot', {
        venue: msg.snapshot.venue,
        marketId: msg.snapshot.marketId,
        outcomeId: msg.snapshot.outcomeId,
        bids: msg.snapshot.bids.length,
        asks: msg.snapshot.asks.length,
      });
      s.applySnapshot(msg.snapshot);
      break;
    case 'book_delta':
      wdebug('rx book_delta', {
        venue: msg.update.venue,
        marketId: msg.update.marketId,
        outcomeId: msg.update.outcomeId,
        bidChanges: msg.update.bidChanges.length,
        askChanges: msg.update.askChanges.length,
      });
      s.applyUpdate(msg.update);
      break;
    case 'latency':
      s.setLatency(msg.venue, { p50: msg.p50, p95: msg.p95, n: msg.n, ts: Date.now() });
      break;
    case 'order_ack':
      s.addOrderAck(msg.ack);
      break;
    case 'fill':
      s.addFill(msg.fill);
      break;
    case 'trade':
      s.addTrade(msg.trade);
      break;
    case 'game_state':
      s.setGameState(msg.state);
      break;
    case 'error':
      // eslint-disable-next-line no-console
      console.warn('[ws] server error', { ref: msg.ref, reason: msg.reason });
      s.addError(msg.reason);
      break;
    case 'vault_test_result':
      s.setVaultTestResult({
        ok: msg.ok,
        ...(msg.balance != null ? { balance: msg.balance } : {}),
        ...(msg.reason ? { reason: msg.reason } : {}),
      });
      break;
    case 'user_kalshi_snapshot':
      s.setKalshiUserSnapshot({
        ...(msg.positions ? { positions: msg.positions } : {}),
        ...(msg.openOrders ? { openOrders: msg.openOrders } : {}),
        ...(msg.reason ? { reason: msg.reason } : {}),
      });
      break;
    case 'vault_polymarket_l2_result':
      // The derived creds (apiKey/secret/passphrase) must NOT enter the
      // store — they need to go through the vault's master-key encrypt
      // flow. We dispatch the raw message via a CustomEvent so
      // polymarket-actions.ts can pick it up and persist with the master
      // key, then update the store flags afterwards.
      document.dispatchEvent(new CustomEvent('ladderbook:polymarket-l2-result', { detail: msg }));
      break;
    case 'vault_test_polymarket_result':
      s.setPolymarketTestResult({
        ok: msg.ok,
        ...(msg.balanceUsd != null ? { balanceUsd: msg.balanceUsd } : {}),
        ...(msg.allowanceOk != null ? { allowanceOk: msg.allowanceOk } : {}),
        ...(msg.reason ? { reason: msg.reason } : {}),
      });
      break;
    case 'user_polymarket_snapshot':
      s.setPolymarketUserSnapshot({
        ...(msg.positions ? { positions: msg.positions } : {}),
        ...(msg.openOrders ? { openOrders: msg.openOrders } : {}),
        ...(msg.reason ? { reason: msg.reason } : {}),
      });
      break;
  }
}

export function send(msg: ClientMsg): boolean {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    wdebug('send skipped (ws not open)', { t: msg.t });
    return false;
  }
  wdebug('send', { t: msg.t, ...summariseClient(msg) });
  ws.send(JSON.stringify(msg));
  return true;
}

// Best-effort interesting bits — keeps the debug log compact but
// useful enough to follow a subscribe/unsubscribe lifecycle.
function summariseClient(msg: ClientMsg): Record<string, unknown> {
  switch (msg.t) {
    case 'subscribe':
    case 'unsubscribe':
      return { venue: msg.venue, marketId: msg.marketId, outcomeId: msg.outcomeId };
    case 'subscribe_game':
    case 'unsubscribe_game':
      return { gameId: msg.gameId };
    case 'submit_order':
      return { venue: msg.order.venue, clientOrderId: msg.order.clientOrderId };
    case 'cancel_order':
      return { venue: msg.venue, clientOrderId: msg.clientOrderId };
    default:
      return {};
  }
}
